// Form-templates table columns — the domain cells (form identity, published
// date, status badges, relative last-entry, entry count, folder chip, created-by /
// last-updated-by avatars) plus the trailing row-actions kebab, rendered inside
// the common DataTable.

import { useTranslation } from 'react-i18next';
import { Avatar, Chip, Icon, type DataTableColumn } from '@/components/common';
import type { FormPerson, FormTemplate } from '@/api/endpoints/formsApi';
import i18n from '@/i18n/config';
import FormRowActions from './FormRowActions';
import initialsOf from '@/utils/initials';
import toneOf from '@/utils/avatarTone';
import { fmtDate, fmtRelative } from '@/utils/formatDate';

// Deterministic per-person avatar tone so the same person is always one color.

const dash = <span className='forms-table__dash'>—</span>;

// Status cell — badges derived from the lifecycle + the version ids (same rules
// as the legacy list): Suspended stands alone; otherwise Published (has a live
// version) or New (never published), plus Draft whenever the working version
// isn't the published one — so a row can read "Published + Draft" / "New + Draft".
const StatusBadges = ({ form, t }: { form: FormTemplate; t: (key: string) => string }) => {
  if (form.status === 'suspended')
    return (
      <Chip variant='warning' iconStart='circle-pause'>
        {t('forms_status_suspended')}
      </Chip>
    );
  const draft = form.currentVersion !== form.publishedVersion;
  return (
    <span className='forms-table__status'>
      {form.publishedVersion ? (
        <Chip variant='success' iconStart='circle-check'>
          {t('forms_status_published')}
        </Chip>
      ) : (
        <Chip variant='info' iconStart='sparkles'>
          {t('forms_status_new')}
        </Chip>
      )}
      {draft && (
        <Chip variant='neutral' iconStart='pencil'>
          {t('forms_status_draft')}
        </Chip>
      )}
    </span>
  );
};

const PersonCell = ({ person }: { person: FormPerson | null }) =>
  person ? (
    <span className='forms-table__person'>
      <Avatar
        initials={initialsOf(person.fullName)}
        size={28}
        tone={toneOf(person.id || person.fullName)}
      />
      <span className='forms-table__person-name'>{person.fullName}</span>
    </span>
  ) : (
    dash
  );

interface UseFormTemplateColumnsArgs {
  /** Row kebab's Edit — opens the form in the builder. Verb-gated by the page:
   *  absent = hidden in the kebab (same for every optional callback here). */
  onEdit?: (form: FormTemplate) => void;
  /** Row kebab's Bookmark — PATCH /forms/bookmark for this one form. Always
   *  present (per-user favourite): the kebab's permanent item. */
  onBookmark: (form: FormTemplate) => void;
  /** On the Bookmarks tab every row is bookmarked by definition of the filter, so
   *  the kebab offers Remove bookmark instead. Only flips the label + glyph — the
   *  page decides what the shared onBookmark actually sends. */
  bookmarksTab: boolean;
  /** Row kebab's Move to folder — opens the page's folder picker for this form. */
  onMoveFolder?: (form: FormTemplate) => void;
  /** Supplied by the page only when the matching verb is held
   *  (Duplicate→create, Delete→delete). Duplicate runs POST
   *  /forms/duplicate/:id, off the Archived tab; Delete runs POST
   *  /forms/bulk-delete and is Archived-tab only — deleting a form is
   *  archive-first, the People rule. */
  onDuplicate?: (form: FormTemplate) => void;
  onDelete?: (form: FormTemplate) => void;
  /** The lifecycle verbs (PATCH /forms/bulk-action), all riding edit and
   *  tab-gated by the page: Suspend/Activate + Archive off the Archived tab,
   *  Restore on it. The kebab swaps Suspend↔Activate off the row's own status. */
  onSuspendActive?: (form: FormTemplate) => void;
  onArchive?: (form: FormTemplate) => void;
  onRestore?: (form: FormTemplate) => void;
}

export const useFormTemplateColumns = ({
  onEdit,
  onBookmark,
  bookmarksTab,
  onMoveFolder,
  onDuplicate,
  onDelete,
  onSuspendActive,
  onArchive,
  onRestore,
}: UseFormTemplateColumnsArgs): DataTableColumn<FormTemplate>[] => {
  const { t } = useTranslation();

  const columns: DataTableColumn<FormTemplate>[] = [
    {
      key: 'name',
      label: t('table_header_form_name'),
      sortable: true,
      width: 'minmax(15rem, 1.6fr)',
      render: (form) => (
        <span className='forms-table__name'>
          <span className='forms-table__icon' aria-hidden='true'>
            <Icon name={form.icon} size={16} />
          </span>
          <span className='forms-table__name-text'>{form.name}</span>
        </span>
      ),
    },
    {
      key: 'published',
      label: t('table_header_published_date'),
      sortable: true,
      render: (form) => <span className='forms-table__muted'>{fmtDate(form.publishedDate)}</span>,
    },
    {
      key: 'status',
      label: t('table_header_status'),
      sortable: true,
      render: (form) => <StatusBadges form={form} t={t} />,
    },
    {
      key: 'lastEntry',
      label: t('table_header_last_entry'),
      sortable: true,
      render: (form) => (
        <span className='forms-table__muted'>{fmtRelative(form.lastEntry, t)}</span>
      ),
    },
    {
      key: 'entries',
      label: t('table_header_entries'),
      sortable: true,
      render: (form) =>
        form.entries === null ? (
          dash
        ) : (
          <span className='forms-table__entries'>
            {new Intl.NumberFormat(i18n.language).format(form.entries)}
          </span>
        ),
    },
    {
      // Not sortable — POST /forms/all has no folder orderBy key (and the design
      // shows no chevron here).
      key: 'folder',
      label: t('table_header_folder'),
      render: (form) =>
        form.folder ? (
          // Same treatment as the Inspections table's form/project reference
          // cells — an outline chip reads as a pointer to another entity, unlike
          // the filled Status chip beside it.
          <Chip variant='outline' size='lg' iconStart='folder'>
            {form.folder.title}
          </Chip>
        ) : (
          dash
        ),
    },
    {
      key: 'createdBy',
      label: t('table_header_created_by'),
      render: (form) => <PersonCell person={form.createdBy} />,
    },
    {
      key: 'updatedBy',
      label: t('table_header_updated_by'),
      render: (form) => <PersonCell person={form.lastUpdatedBy} />,
    },
    {
      key: 'actions',
      label: '',
      align: 'end',
      width: '4.5rem',
      render: (form) => (
        <FormRowActions
          onEdit={onEdit && (() => onEdit(form))}
          onBookmark={() => onBookmark(form)}
          bookmarked={bookmarksTab}
          onMoveFolder={onMoveFolder && (() => onMoveFolder(form))}
          onDuplicate={onDuplicate && (() => onDuplicate(form))}
          onSuspendActive={onSuspendActive && (() => onSuspendActive(form))}
          suspended={form.status === 'suspended'}
          onArchive={onArchive && (() => onArchive(form))}
          onRestore={onRestore && (() => onRestore(form))}
          onDelete={onDelete && (() => onDelete(form))}
        />
      ),
    },
  ];

  return columns;
};
